import { contrastRatio } from '../platform.ts';
import { DEFAULT_THEME, type ThemeColors } from '../theme.ts';

/** O que uma paleta define; `base` e `sidebar` nulos = as cores de fábrica do beadev. */
export interface PaletteColors {
  base: {
    paper: string;
    sheet: string;
    ink: string;
    muted: string;
    rule: string;
    tonal: string;
  } | null;
  sidebar: { bg: string; text: string; muted: string; rule: string; hover: string } | null;
  dark: boolean;
}

/** Texto em cima da cor de destaque: o que tiver mais contraste, branco ou quase preto. */
export function onAccentColor(accent: string): string {
  return contrastRatio('#ffffff', accent) >= contrastRatio('#0b0e14', accent)
    ? '#ffffff'
    : '#0b0e14';
}

/** Mistura duas cores (0 = só `base`, 1 = só `color`). */
export function mix(color: string, base: string, amount: number): string {
  const a = parseInt(color.slice(1), 16);
  const b = parseInt(base.slice(1), 16);
  const ch = (n: number, s: number) => (n >> s) & 255;
  const out = [16, 8, 0].map((s) => Math.round(ch(a, s) * amount + ch(b, s) * (1 - amount)));
  return `#${out.map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

/** A cor legível sobre os fundos dados: escurece (ou clareia) até o mínimo (4,5 para texto). */
export function readableOn(
  color: string,
  backgrounds: string[],
  lighten: boolean,
  minimum = 4.5,
): string {
  let c = color;
  for (let i = 0; i < 12 && backgrounds.some((bg) => contrastRatio(c, bg) < minimum); i++) {
    c = mix(lighten ? '#ffffff' : '#000000', c, 0.15);
  }
  return c;
}

/** As cores do tema a partir de uma paleta e da cor de destaque. */
export function styleColors(
  spec: PaletteColors,
  accent: string,
  mode: 'light' | 'dark',
): ThemeColors {
  const onAccent = onAccentColor(accent);
  const fallback = DEFAULT_THEME.colors[spec.dark ? 'dark' : mode] as ThemeColors;
  if (!spec.base || !spec.sidebar) {
    // A paleta do beadev: as cores de fábrica, trocando só o que vem do destaque.
    return {
      ...fallback,
      accent,
      'on-accent': onAccent,
      'sidebar-active': accent,
      'sidebar-active-text': onAccent,
      'sidebar-link': readableOn(accent, [fallback.sidebar], true),
    };
  }
  const brandSoft = mix(accent, spec.base.sheet, spec.dark ? 0.22 : 0.12);
  // Avisos e borda dos campos vêm de fábrica e se ajustam ao painel da paleta, até o contraste
  // mínimo do guia (a cor de fábrica que já passa fica como está).
  const onSheet = (c: string, minimum = 4.5) =>
    readableOn(c, [spec.base!.sheet], spec.dark, minimum);
  return {
    ...fallback,
    ...spec.base,
    'control-border': onSheet(spec.dark ? '#6b7385' : '#858c9a', 3),
    danger: onSheet(fallback.danger),
    ok: onSheet(fallback.ok),
    info: onSheet(fallback.info),
    accent,
    'on-accent': onAccent,
    brand: accent,
    'brand-soft': brandSoft,
    'brand-text': readableOn(accent, [spec.base.sheet, brandSoft], spec.dark),
    violet: accent,
    sidebar: spec.sidebar.bg,
    'sidebar-text': spec.sidebar.text,
    'sidebar-muted': spec.sidebar.muted,
    'sidebar-rule': spec.sidebar.rule,
    'sidebar-control': spec.sidebar.rule,
    'sidebar-hover': spec.sidebar.hover,
    'sidebar-active': accent,
    'sidebar-active-text': onAccent,
    'sidebar-link': readableOn(
      accent,
      [spec.sidebar.bg],
      contrastRatio('#ffffff', spec.sidebar.bg) > 4.5,
    ),
  };
}

/**
 * As cores do modo escuro geradas de uma paleta clara (ADR 0128, fase 6). O fundo e os painéis
 * saem de um cinza bem escuro puxado para a cor da lateral da paleta (a "cara" dela); o texto e o
 * apoio são clareados até passar no contraste; a lateral fica um pouco mais escura que o fundo.
 */
export function darkPaletteOf(spec: PaletteColors): PaletteColors {
  if (!spec.base || !spec.sidebar || spec.dark) return spec;
  const tint = spec.sidebar.bg;
  const paper = mix(tint, '#0c0e12', 0.22);
  const sheet = mix(tint, '#161a20', 0.18);
  const ink = mix(tint, '#eef0f3', 0.06);
  const muted = readableOn(mix(tint, '#9aa1ad', 0.1), [paper, sheet], true);
  const side = mix(tint, '#07080a', 0.35);
  const sideText = '#f1f3f5';
  return {
    base: {
      paper,
      sheet,
      ink,
      muted,
      rule: mix('#ffffff', sheet, 0.1),
      tonal: mix(ink, sheet, 0.06),
    },
    sidebar: {
      bg: side,
      text: sideText,
      muted: readableOn(mix(sideText, side, 0.6), [side], true),
      rule: mix(sideText, side, 0.1),
      hover: mix(sideText, side, 0.07),
    },
    dark: true,
  };
}
